import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categoriesAPI, listingsAPI, adsenseAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Search, Plus, User, LogOut, MessageCircle, Globe, ChevronDown, ChevronRight, MapPin, Heart } from 'lucide-react';
import CategoryIcon from '../components/CategoryIcon';
import ResponsiveImage from '../components/ResponsiveImage';
import AnnouncementBanner from '../components/AnnouncementBanner';
import HomeBanners from '../components/HomeBanners';
import { SellerBadge } from '../components/SellerBadge';
import useScrollRestore, { saveFeedScroll } from '../hooks/useScrollRestore';
import useUnreadChatCount from '../hooks/useUnreadChatCount';
import SearchAutocomplete from '../components/SearchAutocomplete';
import HScroller from '../components/HScroller';
import EmailPromptModal from '../components/EmailPromptModal';
import HeaderLocalityDetector from '../components/HeaderLocalityDetector';
import LocationSelector from '../components/LocationSelector';
import useWishlistToggle from '../hooks/useWishlistToggle';
import { HomeAdRow, useGridCols, shouldInsertAdAfter, loadAdSenseScript } from '../components/ads/AdSense';

const LOGO_URL = process.env.PUBLIC_URL + "/branding/genzox-logo.png";

const Homepage = () => {
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  // Homepage-only Google AdSense config (fetched from backend; ads off until enabled).
  const [adsCfg, setAdsCfg] = useState(null);
  const adGridCols = useGridCols();
  useEffect(() => {
    let alive = true;
    adsenseAPI.getConfig()
      .then((res) => {
        if (!alive) return;
        setAdsCfg(res.data);
        if (res.data && res.data.enabled && res.data.publisher_id) {
          loadAdSenseScript(res.data.publisher_id);
        }
      })
      .catch(() => { /* ads must never block the homepage */ });
    return () => { alive = false; };
  }, []);
  const [search, setSearch] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  // iter89 — Profile popup UX (outside-click close + iOS/Android + web).
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!showMenu) return undefined;
    const handleOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setShowMenu(false); };
    // `capture:true` ensures we see the tap even when children stopPropagation
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showMenu]);
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); }
      catch (_e) { window.scrollTo(0, 0); }
    } else {
      navigate('/');
    }
  };
  const [currentTime, setCurrentTime] = useState('');
  const [hoveredCat, setHoveredCat] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [expandedMobileCat, setExpandedMobileCat] = useState(null);
  // Iter50: Listings Near Me state
  const [nearMeActive, setNearMeActive] = useState(false);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [nearMeError, setNearMeError] = useState('');
  // iter134 — Persistent Load More visible in every listings mode
  // (nearby / near-me / default). Fetches the next page from the same
  // endpoint that populated the current view.
  const [pageLimit, setPageLimit] = useState(12);
  const [loadingMore, setLoadingMore] = useState(false);
  // iter138 — hasMore tracks whether the server likely has more listings.
  // When a fetch returns fewer rows than requested, auto-loading stops so
  // we never spin in a loop hammering the API.
  const [hasMore, setHasMore] = useState(true);
  const loadMoreSentinelRef = useRef(null);
  const autoLoadTimerRef = useRef(null);
  // Auto-load (infinite scroll) is capped at 3 automatic fetches; after that the
  // user must click "Load More" manually. Manual clicks do not count toward this cap.
  const AUTO_LOAD_LIMIT = 3;
  const autoLoadCountRef = useRef(0);
  const [autoLoadExhausted, setAutoLoadExhausted] = useState(false);
  const [nearMeCoords, setNearMeCoords] = useState(null);
  const [nearMeRadius, setNearMeRadius] = useState(10);
  // iter117 — Wishlist heart state shared via `useWishlistToggle` hook so
  // Homepage, SearchResults, and CategoryBrowse all use one implementation.
  const { isSaved: isWishlisted, toggle: toggleWishlist } = useWishlistToggle(user);

  // iter109 — User's precise locality (e.g. "Amanora", "Hadapsar",
  // "Magarpatta") shown under the GenzoX logo.
  //
  // Primary resolver: `<HeaderLocalityDetector>` mounted below uses the
  // Google Maps Geocoding *JavaScript SDK* (same technique as the Post-Ad
  // map picker) to read `sublocality_level_1` from the user's exact
  // coordinates — the REST endpoint is blocked by the key's referer
  // restrictions, but the JS SDK works fine under the same key.
  //
  // Fallback (this effect): if the browser has no geolocation, or Google
  // Maps fails to load, we still paint *something* useful via IP-based
  // city lookup (ipwho.is, CORS-enabled, no key needed). The Google-derived
  // sublocality name will overwrite this coarser value the moment it
  // resolves.
  const [currentLocality, setCurrentLocality] = useState('');
  const persistLocality = React.useCallback((name) => {
    if (!name) return;
    // iter129 — Never store a non-Latin locality (Devanagari, etc.). This is
    // a defence-in-depth guardrail: the geocoder is already pinned to English,
    // but if any code path ever slips in a native-language string we drop it
    // on the floor instead of caching Hindi for 6 hours.
    if (/[^\u0000-\u024F\u1E00-\u1EFF]/.test(name)) return;
    setCurrentLocality(name);
    try { localStorage.setItem('genzox_locality', JSON.stringify({ name, ts: Date.now() })); } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => {
    let cancelled = false;
    // iter129 — Bust any cached Hindi/Devanagari locality name from earlier
    // versions. Cache pre-iter110 could hold a device-locale string (e.g.
    // "मुंबई"); once cached it would keep repainting for 6 hours even after
    // the geocoder was locked to English. Reject any cache containing chars
    // outside the basic Latin block (letters, digits, spaces, punctuation).
    const isLatin = (s) => !!s && !/[^\u0000-\u024F\u1E00-\u1EFF]/.test(s);
    // Cached hit — instant paint on subsequent loads
    try {
      const cached = localStorage.getItem('genzox_locality');
      if (cached) {
        const { name, ts } = JSON.parse(cached);
        if (name && isLatin(name) && Date.now() - ts < 6 * 60 * 60 * 1000) {
          setCurrentLocality(name);
        } else if (name && !isLatin(name)) {
          // Non-Latin cache — nuke it so the geocoder rewrites in English.
          try { localStorage.removeItem('genzox_locality'); } catch (_) { /* ignore */ }
        }
      }
    } catch (_) { /* ignore */ }
    // IP-based coarse fallback — only used if the Google Maps resolver
    // hasn't already produced a value.
    (async () => {
      try {
        const r = await fetch('https://ipwho.is/', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const j = await r.json();
        if (j && j.success === false) return;
        const name = j.city || j.region || '';
        if (name && !cancelled) {
          // Only paint if state is still empty — Google will always beat this.
          setCurrentLocality((prev) => prev || name);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Iter49: dismissible first-ad-free promo (sessionStorage so it returns after tab close)
  const [promoDismissed, setPromoDismissed] = useState(() => {
    try { return sessionStorage.getItem('genzox_dismiss_first_ad_promo') === '1'; } catch (_) { return false; }
  });
  const hoverTimeoutRef = React.useRef(null);
  // Fix #6: restore scroll position when returning from a listing detail page
  useScrollRestore('homepage', { ready: listings.length > 0 });

  const handleCatEnter = (cat, e) => {
    clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 224; // w-56 = 14rem = 224px
    const centerX = rect.left + rect.width / 2;
    // Calculate left edge directly (no transform needed)
    let left = centerX - dropdownWidth / 2;
    // Clamp: keep at least 16px from viewport edges
    const minLeft = 16;
    const maxLeft = window.innerWidth - dropdownWidth - 16;
    left = Math.max(minLeft, Math.min(left, maxLeft));
    setDropdownPos({ top: rect.bottom + 4, left });
    setHoveredCat(cat.category_id);
  };

  const handleCatLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredCat(null), 150);
  };

  const handleDropdownEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
  };

  const handleDropdownLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredCat(null), 150);
  };

  useEffect(() => {
    // Hydrate from cache instantly (perf), then refresh in background
    try {
      const cached = localStorage.getItem('genzox_categories_v2');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed?.data) && parsed.data.length > 0) {
          setCategories(parsed.data);
        }
      }
      // Iter49: drop the stale v1 cache so users on old order get fresh data immediately
      localStorage.removeItem('genzox_categories_v1');
    } catch (err) { console.warn('Categories cache read failed:', err); }
    loadCategories();
    loadListings();
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadCategories = async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data);
      try { localStorage.setItem('genzox_categories_v2', JSON.stringify({ data: res.data, ts: Date.now() })); } catch (err) { console.warn('Categories cache write failed:', err); }
    } catch (e) { console.error('Categories load failed:', e); }
  };

  const loadListings = async () => {
    try {
      const res = await listingsAPI.getAll({ limit: 12 });
      setListings(res.data);
      setPageLimit(12);
      setHasMore(true); // iter138 — fresh feed means more pages may exist again
      autoLoadCountRef.current = 0; // reset auto-load cap for the fresh feed
      setAutoLoadExhausted(false);
    } catch (e) { console.error(e); }
  };

  // iter134 — Load More: bumps the page size for whichever data source is
  // currently populating the grid (default feed OR near-me feed). Uses the
  // same underlying endpoints so ordering, filters, and location stay
  // consistent.
  // iter135 — nextLimit is now Math.max(pageLimit, listings.length) + 12
  // so the near-me path (which fetches 30 without touching pageLimit=12)
  // cannot SHRINK the grid on the first click. Previously 30 -> 24 -> 36;
  // now 30 -> 42 -> 54.
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextLimit = Math.max(pageLimit, listings.length) + 12;
    try {
      let fresh;
      if (nearMeActive && nearMeCoords) {
        const res = await listingsAPI.getNearby({
          lat: nearMeCoords.lat, lng: nearMeCoords.lng,
          radius_km: nearMeRadius || 25, limit: nextLimit,
        });
        fresh = res.data || [];
      } else {
        const res = await listingsAPI.getAll({ limit: nextLimit });
        fresh = res.data || [];
      }
      // iter138 — Deduplicate by listing_id so a feed that shifts while the
      // user browses can never render the same card twice.
      const seen = new Set();
      const merged = fresh.filter((l) => {
        const id = l?.listing_id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setListings(merged);
      // If the server returned fewer rows than we asked for, the feed is
      // exhausted — stop auto-loading to avoid pointless repeat calls.
      if (fresh.length < nextLimit) setHasMore(false);
      setPageLimit(nextLimit);
    } catch (e) { console.error('Load more failed:', e); }
    finally { setLoadingMore(false); }
  };

  // iter138 — Auto Load More: when the sentinel (rendered just above the
  // Load More button) scrolls into view, wait 1 second then reuse the exact
  // same handleLoadMore path — no separate endpoint, no duplicate logic.
  // The observer re-arms after every successful load so browsing continues
  // seamlessly; it stops entirely once hasMore flips false (feed exhausted).
  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    // Stop auto-loading once the feed is exhausted OR the 3-auto-load cap is hit.
    if (!el || !hasMore || autoLoadExhausted || listings.length === 0) return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (autoLoadCountRef.current >= AUTO_LOAD_LIMIT) {
          setAutoLoadExhausted(true);
          return;
        }
        if (autoLoadTimerRef.current) clearTimeout(autoLoadTimerRef.current);
        autoLoadTimerRef.current = setTimeout(() => {
          autoLoadCountRef.current += 1;
          if (autoLoadCountRef.current >= AUTO_LOAD_LIMIT) setAutoLoadExhausted(true);
          handleLoadMore();
        }, 1000);
      });
    }, { rootMargin: '250px 0px' });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (autoLoadTimerRef.current) { clearTimeout(autoLoadTimerRef.current); autoLoadTimerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings.length, hasMore, autoLoadExhausted, loadingMore, nearMeActive, nearMeCoords, nearMeRadius]);

  const toggleNearMe = () => {
    if (nearMeActive) {
      // Turn OFF — restore latest listings
      setNearMeActive(false);
      setNearMeError('');
      loadListings();
      return;
    }
    if (!navigator.geolocation) {
      setNearMeError('Location is not supported on this device.');
      return;
    }
    setNearMeLoading(true);
    setNearMeError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          setNearMeCoords({ lat: latitude, lng: longitude });
          // Auto-widen radius until we find at least 6 results (or hit 200 km cap)
          let usedRadius = 10;
          let docs = [];
          for (const r of [10, 25, 50, 100, 200]) {
            const res = await listingsAPI.getNearby({ lat: latitude, lng: longitude, radius_km: r, limit: 30 });
            if (Array.isArray(res.data) && res.data.length >= 6) {
              docs = res.data; usedRadius = r; break;
            }
            // Keep the widest result as fallback
            if (Array.isArray(res.data) && res.data.length > docs.length) { docs = res.data; usedRadius = r; }
          }
          setNearMeRadius(usedRadius);
          setListings(docs);
          // iter135 — Sync pageLimit with the actual near-me fetch size so
          // Load More can never shrink the grid (30 -> 24 regression).
          setPageLimit(Math.max(docs.length, 12));
          setHasMore(true); // iter138 — fresh near-me feed re-enables auto-load
          autoLoadCountRef.current = 0; // reset auto-load cap for the near-me feed
          setAutoLoadExhausted(false);
          setNearMeActive(true);
          if (docs.length === 0) setNearMeError('No listings found near you yet. Try widening your area or check back soon.');
        } catch (err) {
          setNearMeError('Could not fetch nearby listings. Please try again.');
        } finally {
          setNearMeLoading(false);
        }
      },
      (err) => {
        setNearMeLoading(false);
        if (err.code === 1) setNearMeError('Location permission denied. Enable location to see listings near you.');
        else if (err.code === 2) setNearMeError('Location unavailable. Try again in a moment.');
        else if (err.code === 3) setNearMeError('Location request timed out.');
        else setNearMeError('Could not get your location.');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const handleSearch = (q) => {
    const query = typeof q === 'string' ? q : search;
    if (query && query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const getCategoryName = (cat) => lang === 'hi' && cat.name_hi ? cat.name_hi : cat.name;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <EmailPromptModal />
      <HeaderLocalityDetector onResolved={persistLocality} />
      <AnnouncementBanner />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-2xl border-b border-gray-100" data-testid="header">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <a href="/" onClick={handleLogoClick} className="flex flex-col items-start flex-shrink-0 cursor-pointer" data-testid="logo-link">
              <img src={LOGO_URL} alt="GenzoX" width="512" height="512" loading="eager" fetchpriority="high" decoding="async" className="h-16 sm:h-20 md:h-24 w-auto object-contain" />
              {/* iter117 — Locality under logo is shown on DESKTOP only.
                  On mobile/tablet it is rendered in the same horizontal
                  row as the IST time (see below-row block further down) so
                  the two elements sit side-by-side per user request. */}
              {/* iter127 — Locality is now interactive: user can search and
                   change it. Desktop-only (lg+); the mobile row below still
                   sits inline with the IST time chip per prior spec. */}
              <div className="hidden lg:flex items-center -mt-1 sm:-mt-2 pl-1" data-testid="user-locality">
                <LocationSelector
                  currentLabel={currentLocality}
                  onSelect={persistLocality}
                  onDetect={() => {
                    // Kick a fresh geolocation resolve. HeaderLocalityDetector's
                    // watchPosition is passive; a manual detect flow triggers a
                    // one-shot high-accuracy fix.
                    if (!navigator.geolocation) return;
                    navigator.geolocation.getCurrentPosition(
                      () => { /* HeaderLocalityDetector will resolve + persist */ },
                      () => { /* silent */ },
                      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
                    );
                  }}
                />
              </div>
            </a>

            <div className="hidden lg:flex items-center gap-2 flex-1 max-w-xl mx-6">
              <SearchAutocomplete
                testId="search"
                value={search}
                onChange={setSearch}
                onSubmit={handleSearch}
                placeholder={t('search_placeholder')}
                className="flex-1"
                inputClassName="brand-gradient-border-input w-full rounded-xl px-4 py-2.5 text-sm text-[#0A0B10] focus:ring-2 focus:ring-[#ff8500]/10 outline-none transition-all"
              />
              <button data-testid="search-btn" onClick={() => handleSearch()}
                className="bg-[#1800ad] text-white rounded-xl px-4 py-2.5 hover:bg-[#1200a0] transition-colors">
                <Search size={18} />
              </button>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-3">
              {/* IST time — visible on DESKTOP (lg+) inline. iter105: on tablets
                  (md range) we now render it in the below-row block like
                  mobile so the top row has room for search+profile. */}
              <div className="hidden lg:block text-[10px] sm:text-xs text-[#6B7280] font-medium" data-testid="ist-time">{currentTime} IST</div>

              {/* iter63 — Hindi language toggle removed (translations were incomplete). English-only until full i18n is ready. */}

              {/* View Plans — visible to ALL visitors (logged in or out) on every screen */}
              <Link data-testid="view-plans-btn" to="/subscriptions"
                className="flex bg-white border-2 border-[#1800ad] text-[#1800ad] rounded-full px-2.5 py-1 sm:px-4 sm:py-1.5 text-[11px] sm:text-sm font-bold hover:bg-[#f0edff] transition-colors items-center gap-1 sm:gap-1.5 whitespace-nowrap">
                {t('view_plans') || 'View Plans'}
              </Link>
              {/* Post Ad — visible on all screen sizes. PostAd page itself redirects to /login when user is logged out. */}
              <Link data-testid="post-ad-btn" to="/post-ad"
                className="flex bg-[#ff8500] text-white rounded-full px-2.5 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-bold hover:bg-[#e57500] transition-colors shadow-lg shadow-[#ff8500]/20 items-center gap-1 sm:gap-1.5 whitespace-nowrap">
                <Plus size={14} className="sm:w-4 sm:h-4" /> {t('post_ad')}
              </Link>

              {user ? (
                // iter95 — Profile visible on tablet+ (md+). Mobile uses the
                // floating Profile FAB instead. Positioned RIGHT of Post Ad.
                // iter105: `whitespace-nowrap` keeps the user's name on a
                // single line on tablet where the button row is tighter.
                <div className="relative hidden md:block" ref={profileMenuRef}>
                  <button data-testid="user-menu-btn" onClick={() => setShowMenu(!showMenu)}
                    className="flex items-center gap-1.5 bg-[#f0edff] rounded-full px-2 py-1 sm:px-3 sm:py-1.5 hover:bg-[#1800ad]/10 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full" referrerPolicy="no-referrer" />
                    ) : (
                      <User size={16} className="text-[#1800ad]" />
                    )}
                    <span className="hidden sm:block text-sm font-semibold text-[#0A0B10] whitespace-nowrap max-w-[140px] lg:max-w-[200px] truncate">{user.name}</span>
                  </button>
                  {showMenu && (
                    <div
                      data-testid="user-menu-popup"
                      className="absolute right-0 mt-2 w-48 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50 origin-top-right animate-[popupIn_180ms_cubic-bezier(0.16,1,0.3,1)]"
                    >
                      <Link data-testid="profile-link" to="/profile" className="block px-4 py-2 text-sm text-[#0A0B10] hover:bg-[#f0edff] transition-colors" onClick={() => setShowMenu(false)}>
                        My Account
                      </Link>
                      <Link data-testid="dashboard-link" to="/dashboard" className="block px-4 py-2 text-sm text-[#0A0B10] hover:bg-[#f0edff] transition-colors" onClick={() => setShowMenu(false)}>
                        {t('dashboard')}
                      </Link>
                      <Link data-testid="wishlist-link" to="/wishlist" className="block px-4 py-2 text-sm text-[#0A0B10] hover:bg-[#f0edff] transition-colors" onClick={() => setShowMenu(false)}>
                        {t('wishlist')}
                      </Link>
                      {user.is_admin && (
                        <Link data-testid="admin-link" to="/admin" className="block px-4 py-2 text-sm text-[#0A0B10] hover:bg-[#f0edff] transition-colors" onClick={() => setShowMenu(false)}>
                          {t('admin_panel')}
                        </Link>
                      )}
                      <button data-testid="logout-btn" onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2">
                        <LogOut size={14} /> {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link data-testid="login-link" to="/login"
                  className="bg-[#1800ad] text-white rounded-full px-3 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-bold hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20">
                  {t('login')}
                </Link>
              )}
            </div>
          </div>
          {/* iter117 — Below-row on mobile/tablet: locality LEFT, IST time
              RIGHT, sharing one horizontal row per user's alignment request.
              Desktop (lg+) shows the locality under the logo and the IST
              time inline in the top row instead. */}
          <div className="flex lg:hidden items-center justify-between gap-2 -mt-1">
            <div data-testid="user-locality-mobile">
              <LocationSelector
                currentLabel={currentLocality}
                onSelect={persistLocality}
                onDetect={() => {
                  if (!navigator.geolocation) return;
                  navigator.geolocation.getCurrentPosition(
                    () => { /* HeaderLocalityDetector resolves + persists */ },
                    () => { /* silent */ },
                    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
                  );
                }}
              />
            </div>
            <div className="text-[10px] text-[#6B7280] font-medium" data-testid="ist-time-mobile">
              {currentTime} IST
            </div>
          </div>
          <div className="flex lg:hidden items-center gap-2 mt-2">
            <SearchAutocomplete
              testId="mobile-search"
              value={search}
              onChange={setSearch}
              onSubmit={handleSearch}
              placeholder={t('search_placeholder')}
              className="flex-1"
              inputClassName="brand-gradient-border-input w-full rounded-xl px-3 py-2 text-sm text-[#0A0B10] outline-none"
            />
            <button onClick={() => handleSearch()} className="bg-[#1800ad] text-white rounded-xl p-2 hover:bg-[#1200a0]">
              <Search size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* First-time User Free Ad Promo Strip — visible to logged-out users (Iter49). Dismissible per session. */}
      {!user && !promoDismissed && (
        <div data-testid="first-ad-free-promo" className="bg-gradient-to-r from-[#fff4ed] via-[#fffaf3] to-[#f0edff] border-b border-[#ff8500]/20">
          <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 py-3 sm:py-3 flex items-center justify-between gap-2">
            <Link to="/login" className="flex items-center gap-2 flex-1 min-w-0 group" data-testid="first-ad-free-promo-cta">
              <span className="inline-block text-xl sm:text-2xl animate-pulse" aria-hidden="true">🎁</span>
              <span className="text-[15px] sm:text-base font-bold tracking-tight leading-tight">
                <span className="bg-gradient-to-r from-[#1800ad] to-[#ff8500] bg-clip-text text-transparent">Get Your First Ad Free</span>
                <span className="text-[#4B5563] font-semibold"> — Exclusive for New Users</span>
              </span>
              <span className="hidden md:inline-block text-sm font-semibold text-[#ff8500] underline-offset-2 group-hover:underline whitespace-nowrap">Sign up &rarr;</span>
            </Link>
            <button
              onClick={() => { try { sessionStorage.setItem('genzox_dismiss_first_ad_promo', '1'); } catch (_) {} setPromoDismissed(true); }}
              data-testid="first-ad-free-promo-dismiss"
              aria-label="Dismiss"
              className="flex-shrink-0 p-1 rounded-full text-[#9CA3AF] hover:text-[#0A0B10] hover:bg-white/60 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Categories Bar */}
      <div className="bg-white border-b border-gray-100 py-3 sm:py-5" data-testid="categories-section">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12">
          <HScroller ariaLabel="categories" testId="categories-slider" autoSlide autoSlideSpeed={36}>
            <div className="flex justify-start gap-2 sm:gap-4 pb-2 pt-1 px-1 md:px-10">
              {categories.map((cat) => {
              const subs = cat.subcategories || [];
              return (
                <div key={cat.category_id} className="relative" data-testid={`category-${cat.category_id}`}
                  onMouseEnter={(e) => handleCatEnter(cat, e)}
                  onMouseLeave={handleCatLeave}>
                  <div className="flex flex-col items-center gap-1.5 sm:gap-2 min-w-[70px] sm:min-w-[90px] cursor-pointer"
                    onClick={() => {
                      // On mobile: tap to expand subcategories
                      if (window.innerWidth < 768 && subs.length > 0) {
                        setExpandedMobileCat(expandedMobileCat === cat.category_id ? null : cat.category_id);
                      }
                    }}>
                    <Link to={`/category/${cat.category_id}`} onClick={(e) => {
                      if (window.innerWidth < 768 && subs.length > 0) e.preventDefault();
                    }}>
                      <div className={`p-2 sm:p-[11px] rounded-xl sm:rounded-2xl bg-white transition-all duration-300 ${hoveredCat === cat.category_id ? 'scale-105' : ''}`}
                        style={{
                          boxShadow: hoveredCat === cat.category_id
                            ? '0 1px 2px rgba(16,24,40,0.06), 0 4px 10px rgba(16,24,40,0.06), 0 20px 36px -8px rgba(16,24,40,0.14)'
                            : '0 1px 2px rgba(16,24,40,0.06), 0 2px 6px rgba(16,24,40,0.05), 0 12px 28px -6px rgba(16,24,40,0.10)',
                        }}>
                        <CategoryIcon category={cat.category_id} size={window.innerWidth < 640 ? 34 : 44} />
                      </div>
                    </Link>
                    <span className="text-[9px] sm:text-[11px] font-bold text-[#0A0B10] text-center leading-tight max-w-[70px] sm:max-w-[90px]">{getCategoryName(cat)}</span>
                    {subs.length > 0 && (
                      <ChevronDown size={10} className={`text-[#9CA3AF] md:hidden transition-transform ${expandedMobileCat === cat.category_id ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </HScroller>
        </div>
      </div>

      {/* Mobile Subcategory Expand */}
      {expandedMobileCat && (() => {
        const cat = categories.find(c => c.category_id === expandedMobileCat);
        const subs = cat?.subcategories || [];
        if (!subs.length) return null;
        return (
          <div className="md:hidden bg-white border-b border-gray-100 px-3 py-3" data-testid="mobile-subcategory-list">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-[#1800ad]">{getCategoryName(cat)}</span>
              <Link to={`/category/${cat.category_id}`} className="text-[10px] text-[#ff8500] font-bold ml-auto"
                onClick={() => setExpandedMobileCat(null)}>View All</Link>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {subs.map((sub) => (
                <Link key={sub.id} to={`/category/${cat.category_id}?sub=${sub.id}`}
                  data-testid={`mobile-sub-${sub.id}`}
                  onClick={() => setExpandedMobileCat(null)}
                  className="px-3 py-1.5 bg-[#f0edff] text-[#1800ad] rounded-full text-[10px] font-semibold hover:bg-[#1800ad] hover:text-white transition-colors">
                  {lang === 'hi' && sub.name_hi ? sub.name_hi : sub.name}
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Subcategory Dropdown Portal - Desktop only */}
      {hoveredCat && (() => {
        const cat = categories.find(c => c.category_id === hoveredCat);
        const subs = cat?.subcategories || [];
        if (!subs.length) return null;
        return (
          <div
            className="hidden md:block fixed w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-[9999]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
            data-testid="subcategory-dropdown"
          >
            <p className="px-3 py-1.5 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{getCategoryName(cat)}</p>
            {subs.map((sub) => (
              <Link key={sub.id} to={`/category/${cat.category_id}?sub=${sub.id}`}
                data-testid={`subcategory-${sub.id}`}
                className="block px-3 py-2 text-xs text-[#0A0B10] hover:bg-[#f0edff] hover:text-[#1800ad] transition-colors font-medium"
                onClick={() => setHoveredCat(null)}>
                {lang === 'hi' && sub.name_hi ? sub.name_hi : sub.name}
              </Link>
            ))}
          </div>
        );
      })()}

      {/* Admin Hero Banner (homepage_hero) — only renders if active banners exist */}
      <HomeBanners position="homepage_hero" />

      {/* Hero Section — slightly more top padding than the previous iteration
          so the headline sits in the upper-middle of the orange band (not
          glued to the top edge) while preserving generous breathing space
          before the CTA via mb-6/sm:mb-8. */}
      <div className="pt-5 sm:pt-8 pb-6 sm:pb-10" style={{background: 'linear-gradient(-45deg, #e89900 0%, #ff751f 100%)'}} data-testid="hero-section">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 text-center">
          <h1 className="text-xl sm:text-3xl lg:text-5xl font-bold tracking-tight text-white mb-6 sm:mb-8 drop-shadow-md">
            {t('brand_tagline')}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {/* iter137 — All three CTAs on a single horizontal row.
                 flex-wrap keeps behaviour safe on very narrow (<340px)
                 devices — pills wrap without clipping. Pill dimensions
                 remain identical across all three (40px tall, px-4/6
                 py-2/2.5) with Post Your Ad's text one step larger fitted
                 via explicit leading-[16px] sm:leading-[20px]. The
                 cta-pulse animation on Post Your Ad is untouched. */}
            <Link
              data-testid="hero-view-plans-btn"
              to="/subscriptions"
              className="inline-block bg-white/95 text-[#1800ad] rounded-full px-4 py-2 sm:px-6 sm:py-2.5 font-bold text-xs sm:text-sm hover:bg-white shadow-lg transition-all"
            >
              View Plans
            </Link>
            <Link data-testid="hero-ready-to-sell-btn" to={user ? "/post-ad" : "/login"}
              className="cta-pulse inline-block bg-white text-[#1800ad] rounded-full px-4 py-2 sm:px-6 sm:py-2.5 font-bold text-sm sm:text-base leading-[16px] sm:leading-[20px] hover:bg-[#f0edff] transition-all duration-300 transform hover:scale-105 shadow-xl">
              Post Your Ad
            </Link>
            <Link
              data-testid="hero-dealer-plans-btn"
              to="/subscriptions#dealer-plans"
              className="inline-block bg-white/95 text-[#1800ad] rounded-full px-4 py-2 sm:px-6 sm:py-2.5 font-bold text-xs sm:text-sm hover:bg-white shadow-lg transition-all"
            >
              Dealer Plans
            </Link>
          </div>
        </div>
      </div>

      {/* Admin Promo Strip Banner (homepage_strip) — only renders if active banners exist */}
      <HomeBanners position="homepage_strip" />

      {/* Listings Grid */}
      <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 py-6 sm:py-12" data-testid="listings-section">
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6 flex-wrap">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-[#0A0B10]">
            {nearMeActive ? 'Listings Near Me' : t('latest_listings')}
            {nearMeActive && nearMeRadius && (
              <span className="ml-2 text-xs sm:text-sm font-medium text-[#9CA3AF]">within {nearMeRadius} km</span>
            )}
          </h2>
          <button
            data-testid="listings-near-me-toggle"
            onClick={toggleNearMe}
            disabled={nearMeLoading}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all ${
              nearMeActive
                ? 'bg-[#1800ad] text-white shadow-md'
                : 'bg-white border-2 border-[#1800ad]/15 text-[#1800ad] hover:border-[#1800ad]/30'
            } ${nearMeLoading ? 'opacity-60 cursor-wait' : ''}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {nearMeLoading ? 'Locating…' : (nearMeActive ? 'Showing nearby' : 'Listings near me')}
          </button>
        </div>
        {nearMeError && (
          <div data-testid="near-me-error" className="mb-4 text-xs sm:text-sm text-[#ff8500] bg-[#fff4ed] border border-[#ff8500]/20 rounded-lg px-3 py-2">
            {nearMeError}
          </div>
        )}
        {/* iter99 — `items-start` prevents grid rows from stretching every
            card to match the tallest sibling. Previously a card with just
            a Chat-with-Seller button (no PRO/BOOSTED badges, single-line
            address) would still expand to the height of a taller row-mate,
            leaving visible empty space between its content and its border.
            Now each card takes its natural content height and the vertical
            gap between rows becomes visually consistent. */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-5 items-start">
          {listings.map((listing, index) => {
            // iter89 — Chat with Seller: guest → /login (with return path so we
            // come back to the listing); logged-in → go to the listing detail
            // where the existing chat button spawns the conversation. We use a
            // sibling <button> outside the Link so its click doesn't bubble to
            // the card navigation.
            const chatDeep = `/listing/${listing.listing_id}?chat=1`;
            const handleChatClick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (user) {
                navigate(chatDeep);
              } else {
                navigate(`/login?next=${encodeURIComponent(chatDeep)}`);
              }
            };
            const adAfter = shouldInsertAdAfter(index, adGridCols, adsCfg);
            return (
            <React.Fragment key={listing.listing_id}>
            <div className="relative">
            {/* iter117 — Wishlist heart overlay. Absolute-positioned inside
                the card's relative wrapper so it sits over the image without
                shifting any existing layout. onClick uses stopPropagation
                so the card's Link doesn't fire. */}
            <button
              type="button"
              onClick={(e) => toggleWishlist(e, listing.listing_id)}
              aria-label={isWishlisted(listing.listing_id) ? 'Remove from wishlist' : 'Add to wishlist'}
              data-testid={`wishlist-toggle-${listing.listing_id}`}
              className="absolute top-2 right-2 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center hover:bg-white active:scale-90 transition-all"
            >
              <Heart
                size={17}
                strokeWidth={2.2}
                className={isWishlisted(listing.listing_id) ? 'text-red-500 fill-red-500' : 'text-gray-500'}
              />
            </button>
            <Link to={`/listing/${listing.listing_id}`} data-testid={`listing-card-${listing.listing_id}`}
              onClick={() => saveFeedScroll('homepage')}
              className={`brand-gradient-border-card group flex flex-col rounded-xl sm:rounded-2xl p-2 sm:p-3.5 cursor-pointer ${listing.is_dealer_listing ? 'dealer-card' : ''}`}>
              {listing.images && listing.images.length > 0 ? (
                <div className="w-full h-28 sm:h-44 rounded-lg sm:rounded-xl mb-2 sm:mb-3 overflow-hidden bg-gradient-to-br from-[#f7f6ff] to-[#fff4ed] flex items-center justify-center">
                  {/* iter89 — object-contain instead of object-cover so the whole
                      product is visible without arbitrary cropping. Center-aligned
                      within a subtly-tinted background so tall/short/square
                      product photos all present cleanly. */}
                  <ResponsiveImage
                    path={listing.images[0]}
                    alt={listing.title}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-full h-28 sm:h-44 bg-gray-100 rounded-lg sm:rounded-xl mb-2 sm:mb-3 flex items-center justify-center">
                  <span className="text-gray-400 text-xs sm:text-sm">{t('no_image')}</span>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-xs sm:text-base font-bold text-[#0A0B10] mb-1 line-clamp-2">{listing.title}</h3>
                <p className="text-sm sm:text-xl font-bold text-[#1800ad] mb-1">{'\u20B9'}{listing.price.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-[#9CA3AF]">{listing.location}</p>
              </div>
              {(() => {
                // iter122 — Unified Trust Badge on listing cards. Strict hierarchy:
                // Trusted Dealer > Trusted Seller > Verified User. Only one trust
                // badge renders. PRO / BOOSTED remain independent chips.
                const trust = {
                  is_dealer_listing: listing.is_dealer_listing,
                  is_trusted_seller: listing.is_trusted_seller,
                  phone_verified: listing.seller_phone_verified,
                };
                const hasTrust = trust.is_dealer_listing || trust.is_trusted_seller || trust.phone_verified;
                if (!hasTrust && !listing.is_boosted && !listing.is_pro) return null;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {hasTrust && (
                      <SellerBadge trust={trust} size="sm" testId={`trust-badge-${listing.listing_id}`} />
                    )}
                    {listing.is_pro && (
                      <span className="bg-gradient-to-r from-[#1800ad] to-[#ff8500] text-white text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">PRO</span>
                    )}
                    {listing.is_boosted && (
                      <span className="bg-[#fff4ed] text-[#ff8500] border border-[#ff8500]/20 text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">BOOSTED</span>
                    )}
                  </div>
                );
              })()}
              {/* iter89 — Chat with Seller — visible on every card. Guest users
                  are routed to /login with a `next` param so they land back on
                  the listing after auth to open the chat. */}
              {(!user || user.user_id !== listing.user_id) && (
                <button
                  type="button"
                  onClick={handleChatClick}
                  data-testid={`chat-seller-${listing.listing_id}`}
                  className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white transition-colors rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-bold border border-[#1800ad]/20"
                >
                  <MessageCircle size={13} />
                  Chat with Seller
                </button>
              )}
            </Link>
            </div>
            {adAfter && <HomeAdRow cfg={adsCfg} cols={adGridCols} position={index + 1} />}
            </React.Fragment>
            );
          })}
        </div>

        {/* iter135 — Persistent Load More. When the feed has listings we
             fetch the next page as usual. In the near-me EMPTY state (0
             nearby results) we render a fallback CTA that exits near-me
             and reloads the default feed, so the button is truly always
             visible. */}
        {listings.length > 0 ? (
          <div className="mt-8 text-center">
            {/* iter138 — Invisible sentinel for auto load-more. When this
                 scrolls within 250px of the viewport, handleLoadMore fires
                 after a 1-second delay. The button below stays as the
                 manual fallback. */}
            <div ref={loadMoreSentinelRef} data-testid="load-more-sentinel" className="h-px w-full" aria-hidden="true" />
            <button
              type="button"
              data-testid="load-more-listings-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center justify-center bg-white text-[#1800ad] border-2 border-[#1800ad]/20 hover:border-[#1800ad] hover:bg-[#f0edff] rounded-full px-8 py-2.5 text-sm font-bold shadow-sm transition-all disabled:opacity-60"
            >
              {loadingMore ? 'Loading…' : 'Load More'}
            </button>
          </div>
        ) : nearMeActive && (
          <div className="mt-8 text-center">
            <button
              type="button"
              data-testid="load-more-listings-btn"
              onClick={toggleNearMe}
              className="inline-flex items-center justify-center bg-white text-[#1800ad] border-2 border-[#1800ad]/20 hover:border-[#1800ad] hover:bg-[#f0edff] rounded-full px-8 py-2.5 text-sm font-bold shadow-sm transition-all"
            >
              Show latest ads
            </button>
          </div>
        )}
      </div>

      {/* Subscription CTA */}
      <div className="bg-white border-t border-gray-100 py-12" data-testid="plans-promo-section">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0B10] mb-3">{t('ready_to_sell')}</h2>
          <p className="text-base text-[#6B7280] mb-6">{t('choose_plan')}</p>
          <Link data-testid="view-plans-cta" to="/subscriptions"
            className="inline-block bg-[#ff8500] text-white rounded-full px-10 py-3.5 font-bold hover:bg-[#e57500] transition-colors shadow-lg shadow-[#ff8500]/20">
            {t('view_plans')}
          </Link>
        </div>
      </div>

      {/* iter130 — Footer moved to global App.js mount (SiteFooter) so it ships on every route. */}
    </div>
  );
};

export default Homepage;
